const { formatLastSeen } = require('./tempChartDevices');

const CHART_HOUR_CHOICES = [1, 2, 4, 8, 12, 24];

function formatSampleInterval(seconds) {
  const n = Number(seconds);
  if (!Number.isFinite(n) || n <= 0) return '';
  if (n >= 86400 && n % 86400 === 0) {
    const days = n / 86400;
    return days === 1 ? '1 day' : `${days} days`;
  }
  if (n >= 3600 && n % 3600 === 0) {
    const hours = n / 3600;
    return hours === 1 ? '1 hour' : `${hours} hours`;
  }
  if (n >= 60 && n % 60 === 0) {
    return `${n / 60} min`;
  }
  return `${n} sec`;
}

function sensorSeenLine(lastSeen, intervalSec) {
  const seen = formatLastSeen(lastSeen);
  const interval = formatSampleInterval(intervalSec);
  if (!interval) return seen;
  return `${seen} · ${interval}`;
}

function deviceKeys(device) {
  return new Set(
    [device && device.device_id, device && device.device_name]
      .filter((value) => value != null && value !== '')
      .map(String),
  );
}

function sensorNumber(row) {
  if (row && row.probe != null && row.probe !== '') return String(row.probe);
  if (row && row.probe_id != null && row.probe_id !== '') {
    return String(row.probe_id);
  }
  return '';
}

function latestProbeTemp(device, rows, probeId) {
  const keys = deviceKeys(device);
  let best = null;
  for (const row of rows || []) {
    if (!keys.has(String(row.device_id))) continue;
    if (sensorNumber(row) !== String(probeId)) continue;
    const time = Date.parse(row.reading_time);
    const stamp = Number.isNaN(time) ? 0 : time;
    if (best == null || stamp >= best.stamp) {
      best = { stamp, temp: row.temp_f };
    }
  }
  if (!best || best.temp == null || best.temp === '') return null;
  const temp = Number(best.temp);
  return Number.isNaN(temp) ? null : temp;
}

const SELECTED_SENSOR_KEY = 'propertySensorsSelectedId';

function rememberedSensorId(devices, storedId, currentId) {
  if (currentId != null && currentId !== '') return currentId;
  if (storedId == null || storedId === '') return null;
  const match = (devices || []).find(
    (device) => String(device.id) === String(storedId),
  );
  return match ? match.id : null;
}

function probeLineWithOutside(probes, outsideTemp) {
  const line = (probes || []).map((probe) => ({ ...probe }));
  const temp = Number(outsideTemp);
  if (outsideTemp == null || outsideTemp === '' || !Number.isFinite(temp)) {
    return line;
  }
  const outside = { key: 'outside', name: 'outside', temp };
  const ambientAt = line.findIndex(
    (probe) => String(probe.name).trim().toLowerCase() === 'ambient',
  );
  const insertAt = ambientAt === -1 ? line.length : ambientAt + 1;
  line.splice(insertAt, 0, outside);
  return line;
}

function formatProbeTemp(value) {
  if (value == null || value === '') return '—';
  const temp = Number(value);
  if (Number.isNaN(temp)) return '—';
  return `${temp.toFixed(1)}°F`;
}

function chartWindowSince(now, hours) {
  const when = now instanceof Date ? now : new Date(now);
  const spanHours = CHART_HOUR_CHOICES.includes(Number(hours))
    ? Number(hours)
    : 8;
  return new Date(when.getTime() - spanHours * 60 * 60 * 1000);
}

function eightHourSinceIso(now = new Date()) {
  return chartWindowSince(now, 8).toISOString();
}

function chartRefreshMs(intervalSec) {
  const interval = Number(intervalSec);
  const step = Number.isFinite(interval) && interval > 0 ? interval : 60;
  return Math.round(step * 1000);
}

function chartReadingLimit(hours, intervalSec) {
  const spanHours = CHART_HOUR_CHOICES.includes(Number(hours))
    ? Number(hours)
    : 8;
  const interval = Number(intervalSec);
  const step = Number.isFinite(interval) && interval > 0 ? interval : 60;
  const perProbe = Math.ceil((spanHours * 3600) / step);
  return Math.min(perProbe * 2, 50000);
}

function chartSinceParam(date) {
  const when = date instanceof Date ? date : new Date(date);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Detroit',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(when);
  const read = (type) =>
    parts.find((part) => part.type === type)?.value || '00';
  const hour = read('hour') === '24' ? '00' : read('hour');
  return `${read('year')}-${read('month')}-${read('day')} ${hour}:${read('minute')}:${read('second')}`;
}

function probePoints(readings, probeId) {
  return (readings || [])
    .filter(
      (row) => sensorNumber(row) === String(probeId) && row.temp_f != null,
    )
    .map((row) => ({
      t: Date.parse(row.reading_time),
      f: Number(row.temp_f),
    }))
    .filter((point) => !Number.isNaN(point.t) && !Number.isNaN(point.f))
    .sort((a, b) => a.t - b.t);
}

function plotLeft(pad, labelWidth) {
  return pad + (labelWidth || 0);
}

function yForTemp(temp, min, max, height, pad) {
  return pad + (1 - (temp - min) / (max - min)) * (height - pad * 2);
}

function niceStep(span) {
  const safe = Math.max(span, 0.1);
  const raw = safe / 4;
  const pow = 10 ** Math.floor(Math.log10(raw));
  const fraction = raw / pow;
  let nice = 1;
  if (fraction >= 5) nice = 5;
  else if (fraction >= 2) nice = 2;
  return nice * pow;
}

function formatTick(value, step) {
  const text = step < 1 ? value.toFixed(1) : String(Math.round(value));
  return text.replace(/\.0$/, '');
}

function scaleGrid(min, max, width, height, pad, labelWidth) {
  const step = niceStep(max - min);
  const left = plotLeft(pad, labelWidth);
  const right = width - pad;
  const first = Math.ceil((min - step * 1e-6) / step) * step;
  const ticks = [];
  for (let i = 0; i < 8; i += 1) {
    const value = first + i * step;
    if (value > max + step * 0.01) break;
    if (value < min - step * 0.01) continue;
    ticks.push({
      value,
      label: formatTick(value, step),
      y: Number(yForTemp(value, min, max, height, pad).toFixed(1)),
      x1: left,
      x2: right,
    });
  }
  return ticks;
}

function linePath(points, min, max, t0, t1, width, height, pad, labelWidth) {
  if (!points.length) return '';
  const span = Math.max(t1 - t0, 1);
  const left = plotLeft(pad, labelWidth);
  const right = width - pad;
  return points
    .map((point, index) => {
      const x = left + ((point.t - t0) / span) * (right - left);
      const y = yForTemp(point.f, min, max, height, pad);
      return `${index === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

function buildSparkline(
  readings,
  width = 320,
  height = 120,
  pad = 4,
  windowStart,
  windowEnd,
  labelWidth = 0,
) {
  const sensor1Points = probePoints(readings, 1);
  const sensor2Points = probePoints(readings, 2);
  const all = sensor1Points.concat(sensor2Points);
  if (!all.length) {
    return { empty: true, sensor1: '', sensor2: '', grid: [] };
  }
  let min = Math.min(...all.map((point) => point.f));
  let max = Math.max(...all.map((point) => point.f));
  if (min === max) {
    min -= 1;
    max += 1;
  } else {
    const cushion = Math.max((max - min) * 0.15, 0.4);
    min -= cushion;
    max += cushion;
  }
  const start = Date.parse(windowStart);
  const end = Date.parse(windowEnd);
  const t0 = Number.isNaN(start)
    ? Math.min(...all.map((point) => point.t))
    : start;
  const t1 = Number.isNaN(end) ? Math.max(...all.map((point) => point.t)) : end;
  return {
    empty: false,
    sensor1: linePath(
      sensor1Points,
      min,
      max,
      t0,
      t1,
      width,
      height,
      pad,
      labelWidth,
    ),
    sensor2: linePath(
      sensor2Points,
      min,
      max,
      t0,
      t1,
      width,
      height,
      pad,
      labelWidth,
    ),
    grid: scaleGrid(min, max, width, height, pad, labelWidth),
  };
}

module.exports = {
  latestProbeTemp,
  formatProbeTemp,
  probeLineWithOutside,
  SELECTED_SENSOR_KEY,
  rememberedSensorId,
  eightHourSinceIso,
  CHART_HOUR_CHOICES,
  chartWindowSince,
  chartReadingLimit,
  chartRefreshMs,
  chartSinceParam,
  buildSparkline,
  sensorSeenLine,
};
